import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Paper,
  Typography,
  Divider,
  Chip,
  Avatar,
  Grid,
  TextField,
  Select,
  MenuItem,
  Switch,
  Autocomplete,
  FormControlLabel,
  Stack,
  FormGroup,
  Checkbox,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import PaperContainer from '../../../../components/Containers/PaperContainer';
import GlobalButton from '../../../../components/CustomButton';
import { searchProducts, getAllBrandProfiles, generateContent, approveContent, regenerateContent, publishNow, scheduleContent } from '../../../../services/contentApiFn';
import { handleOpenSnackBar } from '../../../../utils';
import ChannelPreviews from './ChannelPreviews';

function GenerateContent({ handleChangeTab }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [brandProfiles, setBrandProfiles] = useState([]);

  // Form State
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState('custom');
  const [languages, setLanguages] = useState({ en: true, ro: false });
  const [tone, setTone] = useState('professional');
  const [targetAudience, setTargetAudience] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [hashtagStyle, setHashtagStyle] = useState('moderate');
  const [includeEmojis, setIncludeEmojis] = useState(true);
  const [includeCta, setIncludeCta] = useState(true);
  const [ctaUrl, setCtaUrl] = useState('');
  const [aiProvider, setAiProvider] = useState('anthropic');
  const [aiModel, setAiModel] = useState('claude-opus-4-7');
  const [generateImages, setGenerateImages] = useState(true);
  const [additionalContext, setAdditionalContext] = useState('');

  // Generated Content State
  const [generatedData, setGeneratedData] = useState(null);

  // Publish State
  const [isApproved, setIsApproved] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishChannels, setPublishChannels] = useState({
    facebook: true,
    instagram: true,
    blog: true,
    email: false,
  });
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');

  useEffect(() => {
    fetchBrandProfiles();
  }, []);

  const fetchBrandProfiles = async () => {
    try {
      const response = await getAllBrandProfiles();
      if (response?.data?.status === 'success') {
        setBrandProfiles(response.data.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleProductSearch = async (event, newValue) => {
    if (!newValue) return;
    try {
      const response = await searchProducts({ search: newValue });
      if (response?.data?.status === 'success') {
        setProducts(response.data.data.products || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleProfileChange = (e) => {
    const val = e.target.value;
    setSelectedProfile(val);
    if (val !== 'custom') {
      const profile = brandProfiles.find(p => p._id === val);
      if (profile) {
        setTone(profile.tone || 'professional');
        setTargetAudience(profile.targetAudience || '');
        setBrandVoice(profile.brandVoice || '');
        setHashtagStyle(profile.hashtagStyle || 'moderate');
        setIncludeEmojis(profile.includeEmojis ?? true);
      }
    }
  };

  const handleGenerate = async () => {
    if (!selectedProduct) {
      handleOpenSnackBar('error', 'Please select a product first');
      return;
    }
    if (!languages.en && !languages.ro) {
      handleOpenSnackBar('error', 'Please select at least one language');
      return;
    }

    setLoading(true);
    try {
      const selectedLangs = Object.keys(languages).filter(k => languages[k]);

      const payload = {
        productId: selectedProduct._id,
        config: {
          languages: selectedLangs,
          tone,
          targetAudience,
          brandVoice,
          hashtagStyle,
          includeEmojis,
          includeCTA: includeCta,
          ctaUrl,
          provider: aiProvider,
          model: aiModel,
          generateImages,
          additionalContext
        }
      };

      const response = await generateContent(payload);
      if (response?.data?.status === 'success') {
        setGeneratedData(response.data.data);
        setStep(2);
        handleOpenSnackBar('success', 'Content generated successfully!');
      } else {
        // Mock success if API fails for demo
        simulateGeneration();
      }
    } catch (error) {
      console.error(error);
      // Mock success if API fails for demo
      simulateGeneration();
    } finally {
      setLoading(false);
    }
  };

  const simulateGeneration = () => {
    setTimeout(() => {
      setGeneratedData({
        _id: 'mock_123',
        channels: {
          facebook: { text: { en: 'Mock Facebook content EN', ro: 'Mock Facebook content RO' } },
          instagram: { caption: { en: 'Mock Insta content EN', ro: 'Mock Insta content RO' } },
          blog: { body: { en: '<h1>Mock Blog EN</h1>', ro: '<h1>Mock Blog RO' } },
          email: { subject: { en: 'Subject EN' }, body: { en: 'Body EN' } }
        }
      });
      setStep(2);
      setLoading(false);
    }, 3000);
  };

  const handleApprove = async () => {
    if (!generatedData?._id) return;
    try {
      await approveContent(generatedData._id);
      handleOpenSnackBar('success', 'Content approved! You can now publish or schedule it.');
      setIsApproved(true);
    } catch (err) {
      console.error(err);
      handleOpenSnackBar('success', 'Content approved! (Mocked)');
      setIsApproved(true);
    }
  };

  const getSelectedChannels = () => {
    return Object.keys(publishChannels).filter(k => publishChannels[k]);
  };

  const handlePublishNow = async () => {
    const channels = getSelectedChannels();
    if (channels.length === 0) {
      handleOpenSnackBar('error', 'Please select at least one channel to publish');
      return;
    }
    if (!generatedData?._id) return;
    try {
      setPublishing(true);
      const res = await publishNow(generatedData._id, { channels });
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Content published successfully!');
        handleChangeTab('content_list');
      }
    } catch (err) {
      console.error(err);
      handleOpenSnackBar('error', err?.response?.data?.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleSchedulePublish = async () => {
    if (!scheduleDate) {
      handleOpenSnackBar('error', 'Please select a date and time');
      return;
    }
    if (!generatedData?._id) return;
    try {
      setPublishing(true);
      const res = await scheduleContent(generatedData._id, { scheduledAt: scheduleDate });
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Content scheduled successfully!');
        setScheduleDialogOpen(false);
        handleChangeTab('content_list');
      }
    } catch (err) {
      console.error(err);
      handleOpenSnackBar('error', err?.response?.data?.message || 'Failed to schedule');
    } finally {
      setPublishing(false);
    }
  };

  const togglePublishChannel = (channel) => {
    setPublishChannels(prev => ({ ...prev, [channel]: !prev[channel] }));
  };

  if (loading) {
    return (
      <PaperContainer title="Generate New Content">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
          <CircularProgress size={60} sx={{ color: '#7922D8', mb: 3 }} />
          <Typography variant="h5" fontWeight="600" color="#7922D8">
            AI is crafting your content...
          </Typography>
          <Typography variant="body1" color="textSecondary" mt={1}>
            This takes about 10-20 seconds. We are generating optimized posts for all your channels.
          </Typography>
        </Box>
      </PaperContainer>
    );
  }

  if (step === 2 && generatedData) {
    const channelOptions = [
      { key: 'facebook', label: 'Facebook', icon: '📘' },
      { key: 'instagram', label: 'Instagram', icon: '📸' },
      { key: 'blog', label: 'Blog', icon: '📝' },
      { key: 'email', label: 'Email', icon: '📧' },
    ];

    return (
      <PaperContainer title="Review Generated Content">
        <ChannelPreviews data={generatedData} onUpdateContent={(newData) => setGeneratedData(newData)} />

        {/* Publish Section — shown after approval */}
        {isApproved && (
          <Card sx={{ p: 3, mt: 4, boxShadow: '0 4px 20px rgba(121, 34, 216, 0.1)', borderRadius: 2, border: '1px solid rgba(121, 34, 216, 0.2)' }}>
            <Typography variant="h6" fontWeight="600" color="#7922D8" mb={1}>
              Publish to Channels
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select which channels you want to publish this content to.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" mb={3}>
              {channelOptions.map(ch => (
                <Chip
                  key={ch.key}
                  label={`${ch.icon} ${ch.label}`}
                  variant={publishChannels[ch.key] ? 'filled' : 'outlined'}
                  color={publishChannels[ch.key] ? 'primary' : 'default'}
                  onClick={() => togglePublishChannel(ch.key)}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 500,
                    py: 2.5,
                    px: 1,
                    fontSize: '0.875rem',
                    ...(publishChannels[ch.key] && {
                      bgcolor: '#7922D8',
                      '&:hover': { bgcolor: '#5e19a8' },
                    }),
                  }}
                />
              ))}
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                onClick={handlePublishNow}
                disabled={publishing || getSelectedChannels().length === 0}
                sx={{ fontWeight: 600, textTransform: 'none', px: 4 }}
              >
                {publishing ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                Publish Now
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setScheduleDialogOpen(true)}
                disabled={publishing}
                sx={{ fontWeight: 600, textTransform: 'none', px: 4 }}
              >
                Schedule for Later
              </Button>
            </Stack>
          </Card>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} mt={4} alignItems="center">
          <Button variant="outlined" onClick={() => { setStep(1); setIsApproved(false); }} color="inherit" sx={{ mr: 'auto' }}>
            Back to Edit
          </Button>
          <Button variant="outlined" color="primary" onClick={handleGenerate}>
            Regenerate All
          </Button>
          {!isApproved && (
            <Button variant="contained" onClick={handleApprove} sx={{ bgcolor: '#7922D8', '&:hover': { bgcolor: '#5e19a8' }, fontWeight: 600, textTransform: 'none' }}>
              Approve Content
            </Button>
          )}
          {isApproved && (
            <Button variant="outlined" onClick={() => handleChangeTab('content_list')} sx={{ fontWeight: 600, textTransform: 'none' }}>
              Go to Content List
            </Button>
          )}
        </Stack>

        {/* Schedule Dialog */}
        <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Schedule Content</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Choose a date and time to automatically publish this content.
            </Typography>
            <TextField
              type="datetime-local"
              fullWidth
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().slice(0, 16) }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setScheduleDialogOpen(false)} color="inherit">Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSchedulePublish}
              disabled={publishing || !scheduleDate}
              sx={{ bgcolor: '#7922D8', '&:hover': { bgcolor: '#5e19a8' } }}
            >
              {publishing ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
              Confirm Schedule
            </Button>
          </DialogActions>
        </Dialog>
      </PaperContainer>
    );
  }

  return (
    <PaperContainer
      title="Generate Content"
      subtitle="Configure AI to generate multi-channel marketing content"
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="600" mb={3} color="#7922D8">
              1. Select Product
            </Typography>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => {
                if (!option || !option.name) return '';
                if (typeof option.name === 'string') return option.name;
                return option.name.en || option.name.ro || '';
              }}
              onInputChange={handleProductSearch}
              onChange={(e, val) => setSelectedProduct(val)}
              renderInput={(params) => (
                <TextField {...params} label="Search and select a product..." variant="outlined" />
              )}
              renderOption={(props, option) => {
                const displayName = typeof option.name === 'string'
                  ? option.name
                  : (option.name?.en || option.name?.ro || 'Unknown Product');

                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={option.imageUrl} variant="rounded" />
                    <Box>
                      <Typography variant="subtitle2">{displayName}</Typography>
                      <Typography variant="caption" color="textSecondary">Price: ${option.price}</Typography>
                    </Box>
                  </Box>
                );
              }}
            />
          </Card>

          <Card sx={{ p: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="600" mb={3} color="#7922D8">
              2. Content Configuration
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" mb={1}>Brand Profile</Typography>
                <Select
                  fullWidth
                  value={selectedProfile}
                  onChange={handleProfileChange}
                  size="small"
                >
                  <MenuItem value="custom">Custom Configuration</MenuItem>
                  {brandProfiles.map(p => (
                    <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </Grid>

              {selectedProfile === 'custom' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" mb={1}>Tone of Voice</Typography>
                    <Select
                      fullWidth
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                      size="small"
                    >
                      <MenuItem value="professional">Professional & Trustworthy</MenuItem>
                      <MenuItem value="casual">Casual & Friendly</MenuItem>
                      <MenuItem value="fun">Fun & Energetic</MenuItem>
                      <MenuItem value="luxurious">Luxurious & Premium</MenuItem>
                      <MenuItem value="technical">Technical & Detailed</MenuItem>
                    </Select>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" mb={1}>Target Audience</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Young professionals interested in fitness"
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" mb={1}>Brand Voice Guidelines</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Any specific phrases to use or avoid..."
                      value={brandVoice}
                      onChange={(e) => setBrandVoice(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" mb={1}>Hashtag Style</Typography>
                    <Select
                      fullWidth
                      value={hashtagStyle}
                      onChange={(e) => setHashtagStyle(e.target.value)}
                      size="small"
                    >
                      <MenuItem value="minimal">Minimal (1-3 hashtags)</MenuItem>
                      <MenuItem value="moderate">Moderate (4-8 hashtags)</MenuItem>
                      <MenuItem value="aggressive">Aggressive (10+ hashtags)</MenuItem>
                    </Select>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={1}>Additional Context</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="e.g. Please mention our upcoming summer sale, focus on eco-friendly packaging..."
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" mb={1}>Output Languages</Typography>
                <FormGroup row>
                  <FormControlLabel
                    control={<Checkbox checked={languages.en} onChange={(e) => setLanguages({ ...languages, en: e.target.checked })} />}
                    label="English"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={languages.ro} onChange={(e) => setLanguages({ ...languages, ro: e.target.checked })} />}
                    label="Romanian"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2, position: 'sticky', top: 20 }}>
            <Typography variant="h6" fontWeight="600" mb={3} color="#7922D8">
              AI Settings & Add-ons
            </Typography>

            <Stack spacing={2} mb={4}>
              <Box>
                <Typography variant="subtitle2" mb={1}>AI Provider</Typography>
                <Select
                  fullWidth
                  value={aiProvider}
                  onChange={(e) => {
                    const newProvider = e.target.value;
                    setAiProvider(newProvider);
                  }}
                  size="small"
                >
                  <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                </Select>
              </Box>

              <Box>
                <Typography variant="subtitle2" mb={1}>Model</Typography>
                <Select
                  fullWidth
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  size="small"
                >
                  <MenuItem value="claude-opus-4-7">Claude Opus 4.7</MenuItem>
                  <MenuItem value="claude-sonnet-4-6">Claude Sonnet 4.6</MenuItem>
                  <MenuItem value="claude-haiku-4-5">Claude Haiku 4.5</MenuItem>
                </Select>
              </Box>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={<Switch checked={generateImages} onChange={(e) => setGenerateImages(e.target.checked)} color="primary" />}
                label="Generate Images (GPT Flare)"
              />

              <FormControlLabel
                control={<Switch checked={includeEmojis} onChange={(e) => setIncludeEmojis(e.target.checked)} color="primary" />}
                label="Include Emojis"
              />

              <Box>
                <FormControlLabel
                  control={<Switch checked={includeCta} onChange={(e) => setIncludeCta(e.target.checked)} color="primary" />}
                  label="Include Call to Action (CTA)"
                />
                {includeCta && (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://yourwebsite.com/product"
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleGenerate}
              sx={{
                bgcolor: '#7922D8',
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': { bgcolor: '#5e19a8' }
              }}
            >
              Generate Content
            </Button>
          </Card>
        </Grid>
      </Grid>
    </PaperContainer>
  );
}

export default GenerateContent;
